import type { ReactNode } from 'react';
import { makeStyles } from '@material-ui/core/styles';

/*
 * Skin for the org plugin's Members list card.
 *
 * The vendor member card is a BUI `Card` pinned to `height: 140` with an
 * x-large avatar, so a member with no description renders as a name, an email
 * and roughly 70px of empty white. Bringing the row down to its content — 40px
 * avatar, name, email — fits two more members above the fold on a team page and
 * matches the density of the scorecards on the browse surface.
 *
 * As with `OwnershipTiles`, the wrapper is `display: contents` and every rule is
 * a descendant selector, which outweighs the vendor's single-class rules without
 * `!important`. The member cards are matched through `bui-CardBody` so the rules
 * cannot reach the Members list card that CONTAINS them (also a `bui-Card`).
 *
 * Link colour is deliberately absent: the member email inherits from the global
 * link treatment.
 */
const useStyles = makeStyles({
  root: {
    display: 'contents',

    '& [class*="bui-CardBody"] [class*="PluginOrgMembersListCardComponent-memberList"]':
      {
        gridTemplateColumns: 'repeat(auto-fill, minmax(228px, 1fr))',
        gridAutoRows: 'auto',
        gap: 10,
        paddingTop: 14,
      },

    '& [class*="bui-CardBody"] [class*="bui-Card"]': {
      height: 'auto',
      minHeight: 0,
      alignItems: 'center',
      gap: 12,
      padding: 12,
      borderRadius: 12,
      boxShadow: 'var(--hone-portal-elevation-raised)',
      transition: 'box-shadow 120ms ease',
    },
    '& [class*="bui-CardBody"] [class*="bui-Card"]:hover': {
      boxShadow: 'var(--hone-portal-ring-hairline), 0 4px 10px rgba(0, 0, 0, 0.06)',
    },

    '& [class*="bui-CardBody"] [class*="bui-Card"] [class*="bui-Avatar"]': {
      width: 40,
      height: 40,
      minWidth: 40,
      fontSize: 13,
    },

    '& [class*="bui-CardBody"] [class*="bui-Card"] h4': {
      fontSize: 13.5,
      fontWeight: 600,
      lineHeight: 1.35,
      color: 'var(--bui-fg-primary)',
    },

    '& [class*="bui-CardBody"] [class*="bui-Card"] a': {
      fontSize: 12,
      lineHeight: 1.4,
    },
  },
});

export function MemberCards({ children }: { children: ReactNode }) {
  const classes = useStyles();
  return <div className={classes.root}>{children}</div>;
}
